import { useTranslation } from '../../../shared/i18n';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, Tabs, List, Tag, Button, Input, Space, Table, Typography, Empty, Badge, message, Tooltip } from 'antd';
import { GiftOutlined } from '@ant-design/icons';
import moment from 'moment';
import couponService from '../../services/couponService';
import { formatMoney } from '../../../shared/utils/money';
import { userPath } from '../../../shared/config/paths';
import { CouponTicket, couponUsePath } from '../../components/CouponTicket';
import { useActiveCampaigns } from '../../components/CampaignBanner';
import { setPendingCoupon, withCoupon } from '../../utils/pendingCoupon';

const { Text } = Typography;

/** 面值一句话：20% off / $5 off / 1 month free */
export const couponValueText = (c, t) => {
    if (c.type === 'trial') return !c.trial_cycle || c.trial_cycle === 'monthly' ? t('1 month free') : t('1 {{cycle}} period free', { cycle: c.trial_cycle });
    if (c.type === 'percent') return t('{{value}}% off', { value: Number(c.value) });
    return t('{{value}} off', { value: formatMoney(c.value, c.currency) });
};

export const couponScopeText = (c, t) => {
    const products = c.scope?.products || [];
    if (products.length) return products.map((p) => p.name).join(', ');
    if (c.scope?.type === 's3') return t('Object Storage');
    if (c.scope?.type) return c.scope.type.toUpperCase();
    return t('All products');
};

export const couponRenewalText = (c, t) => {
    if (c.renewal_mode === 'forever') return t('Every renewal');
    if (c.renewal_mode === 'periods') return t('First {{n}} periods', { n: c.renewal_periods });
    return t('First invoice');
};

const Coupons = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [tab, setTab] = useState(() => (new URLSearchParams(window.location.search).get('tab') === 'campaign' ? 'campaign' : 'available'));
    const [campaigns] = useActiveCampaigns();
    const [campaignCount, setCampaignCount] = useState(0);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [code, setCode] = useState('');
    const [redeeming, setRedeeming] = useState(false);
    const [summary, setSummary] = useState(null);

    const load = useCallback(async (which = tab) => {
        setLoading(true);
        const r = await couponService.list(which);
        setRows(r?.success ? (r.data || []) : []);
        setLoading(false);
    }, [tab]);

    const loadSummary = () => couponService.summary().then((r) => r?.success && setSummary(r.data));

    useEffect(() => { load(tab); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { loadSummary(); }, []);
    useEffect(() => { couponService.list('campaign').then((r) => r?.success && setCampaignCount(r.total || (r.data || []).length)); }, []);

    const redeem = async () => {
        if (!code.trim()) return;
        setRedeeming(true);
        const r = await couponService.redeem(code.trim());
        setRedeeming(false);
        if (r?.success) {
            message.success(t('Added to your coupons'));
            setCode('');
            setTab('available');
            load('available');
            loadSummary();
        } else {
            message.error(r?.error || t('Coupon not found'));
        }
    };

    const use = (c) => {
        const link = c.use_link || '/billing/cart';
        navigate(userPath(link));
    };

    const expiryTag = (c) => {
        if (!c.expires_at) return <Text type="secondary">{t('No expiry')}</Text>;
        return (
            <Text type={c.expiring_soon ? 'danger' : 'secondary'}>
                {t('Expires {{date}}', { date: moment(c.expires_at).format('YYYY-MM-DD') })}
            </Text>
        );
    };

    const available = (
        <List
            loading={loading}
            grid={{ gutter: 16, xs: 1, sm: 1, md: 2, lg: 3 }}
            dataSource={rows}
            locale={{ emptyText: <Empty description={t('No coupons')} /> }}
            renderItem={(c) => (
                <List.Item>
                    <Card size="small" style={{ borderLeft: '3px solid var(--ant-color-primary, #1677ff)' }}>
                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                            <Space style={{ justifyContent: 'space-between', width: '100%' }}>
                                <Text strong style={{ fontSize: 18 }}>{couponValueText(c, t)}</Text>
                                <Button type="primary" size="small" onClick={() => use(c)}>{t('Use')}</Button>
                            </Space>
                            <Text>{c.name}</Text>
                            <Space size={4} wrap>
                                <Tag>{couponScopeText(c, t)}</Tag>
                                {c.type !== 'trial' && <Tag color="blue">{couponRenewalText(c, t)}</Tag>}
                                {c.max_items ? <Tag>{t('Up to {{n}} items', { n: c.max_items })}</Tag> : null}
                                {c.min_amount ? (
                                    <Tooltip title={t('Minimum spend')}><Tag>≥ {formatMoney(c.min_amount, c.currency)}</Tag></Tooltip>
                                ) : null}
                            </Space>
                            <Space style={{ justifyContent: 'space-between', width: '100%' }}>
                                {expiryTag(c)}
                                <Text type="secondary" copyable={{ text: c.code }} style={{ fontSize: 12 }}>{c.code}</Text>
                            </Space>
                        </Space>
                    </Card>
                </List.Item>
            )}
        />
    );

    // live code campaigns: their public codes as themed tickets (not in the wallet until added)
    const held = new Set(rows.map((r) => r.code));
    const [added, setAdded] = useState([]);
    const codeTickets = campaigns.flatMap((cp) => (cp.codes || []).map((x) => ({ ...x, campaign_name: cp.name, theme: cp.theme, campaign_id: cp.id })))
        .filter((x) => !held.has(x.code)) // already in the wallet (this tab) → shown once, as the wallet ticket
        .map((x) => ({ ...x, in_wallet: x.in_wallet || added.includes(x.code) }));
    const goUse = (c, path) => { setPendingCoupon(c.code); navigate(userPath(path)); };
    const addCode = async (c) => {
        const r = await couponService.redeem(c.code);
        if (r?.success) {
            message.success(t('Added to your coupons'));
            setAdded((a) => [...a, c.code]);
            load('campaign');
            setCampaignCount((n) => n + 1);
            loadSummary();
        } else {
            message.error(r?.error || t('Coupon not found'));
        }
    };
    // every card: ticket + one right-aligned row of same-size antd buttons (default, then primary)
    const ticketCell = (c, actions) => (
        <List.Item>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <CouponTicket c={c} />
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>{actions}</div>
            </div>
        </List.Item>
    );
    const campaignPane = (
        <List
            loading={loading}
            grid={{ gutter: 16, xs: 1, sm: 1, md: 2, lg: 2, xl: 3 }}
            dataSource={[...codeTickets.map((c) => ({ kind: 'code', c })), ...rows.map((c) => ({ kind: 'wallet', c }))]}
            locale={{ emptyText: <Empty description={t('No campaign coupons')} /> }}
            renderItem={({ kind, c }) => (kind === 'code'
                ? ticketCell({ ...c, expires_at: campaigns.find((x) => x.id === c.campaign_id)?.ends_at }, (
                    <>
                        {!c.in_wallet ? <Button type="default" onClick={() => addCode(c)}>{t('Add to my coupons')}</Button> : null}
                        <Button type="primary" onClick={() => goUse(c, withCoupon(c.use_link || '/dashboard', c.code))}>{t('Use now')}</Button>
                    </>
                ))
                : ticketCell({ ...c, scope_text: couponScopeText(c, t) }, (
                    <Button type="primary" onClick={() => goUse(c, couponUsePath({ ...c, use_link: c.use_link }))}>{t('Use now')}</Button>
                )))}
        />
    );

    const usedCols = [
        { title: t('Coupon'), dataIndex: 'name', render: (v, c) => <Space direction="vertical" size={0}><Text>{v}</Text><Text type="secondary" style={{ fontSize: 12 }}>{couponValueText(c, t)}</Text></Space> },
        { title: t('Used at'), dataIndex: 'used_at', render: (v) => (v ? moment(v).format('YYYY-MM-DD HH:mm') : '-') },
        { title: t('Invoice'), dataIndex: 'invoice_id', render: (v, c) => (v ? <Link to={userPath(`/billing/invoices/${v}`)}>{c.invoice_number || `#${v}`}</Link> : '-') },
    ];
    const expiredCols = [
        { title: t('Coupon'), dataIndex: 'name', render: (v, c) => <Space direction="vertical" size={0}><Text>{v}</Text><Text type="secondary" style={{ fontSize: 12 }}>{couponValueText(c, t)}</Text></Space> },
        { title: t('Expired'), dataIndex: 'expires_at', render: (v) => (v ? moment(v).format('YYYY-MM-DD') : '-') },
        { title: t('Status'), dataIndex: 'status', render: (v) => <Tag>{t(v === 'revoked' ? 'Revoked' : v === 'disabled' ? 'Disabled' : 'Expired')}</Tag> },
    ];

    return (
        <Card
            title={<Space><GiftOutlined />{t('My coupons')}</Space>}
            extra={(
                <Space.Compact style={{ width: 260 }}>
                    <Input placeholder={t('Coupon code')} value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} onPressEnter={redeem} allowClear />
                    <Button loading={redeeming} onClick={redeem}>{t('Redeem')}</Button>
                </Space.Compact>
            )}
        >
            <Tabs
                activeKey={tab}
                onChange={setTab}
                items={[
                    {
                        key: 'available',
                        label: (
                            <Badge count={summary?.expiring_soon || 0} size="small" offset={[8, 0]} title={t('Expiring within 7 days')}>
                                {t('Available')}{summary ? ` (${summary.available})` : ''}
                            </Badge>
                        ),
                        children: available,
                    },
                    ...(campaigns.length || campaignCount || tab === 'campaign' ? [{
                        key: 'campaign',
                        label: <span>{t('Campaigns')}{campaignCount ? ` (${campaignCount})` : ''}</span>,
                        children: campaignPane,
                    }] : []),
                    { key: 'used', label: t('Used'), children: <Table rowKey="user_coupon_id" loading={loading} columns={usedCols} dataSource={rows} pagination={{ pageSize: 20 }} size="middle" /> },
                    { key: 'expired', label: t('Expired'), children: <Table rowKey="user_coupon_id" loading={loading} columns={expiredCols} dataSource={rows} pagination={{ pageSize: 20 }} size="middle" /> },
                ]}
            />
        </Card>
    );
};

export default Coupons;
